// frontend/src/App.tsx

import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider as DarkModeProvider } from "./context/ThemeContext";

// Lazy-loaded pages for route-level code splitting
const LoginPage = lazy(() => import("./pages/Login"));
const RegisterPage = lazy(() => import("./pages/Register"));
const VerifyOtpPage = lazy(() => import("./pages/VerifyOtp"));
const StaffRegisterPage = lazy(() => import("./pages/StaffRegister"));
const GoogleAuthCallbackPage = lazy(() => import("./pages/GoogleAuthCallback"));

const DashboardPage = lazy(() => import("./pages/Dashboard"));
const SchedulePage = lazy(() => import("./pages/Schedule"));
const AnalyticsPage = lazy(() => import("./pages/Analytics"));
const ServicesPage = lazy(() => import("./pages/Services"));
const StaffPage = lazy(() => import("./pages/Staff"));
const PaymentMethodsPage = lazy(() => import("./pages/PaymentMethodsPage"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccount"));

const LandingPage = lazy(() => import("./pages/Landing"));
const PublicBookingPage = lazy(() => import("./pages/PublicBooking"));

// Components
import ProtectedRoute from "./components/common/ProtectedRoute";

const RouteLoadingFallback = () => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      bgcolor: "#F8F9FB",
      px: 2,
    }}
  >
    <CircularProgress size={32} sx={{ color: "#4F46E5" }} />
    <Typography sx={{ color: "#6B7280", fontSize: "0.9rem", fontWeight: 500 }}>
      Loading page...
    </Typography>
  </Box>
);

const App = () => {
  return (
    <DarkModeProvider>
      <AuthProvider>
        <Router>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-otp" element={<VerifyOtpPage />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallbackPage />} />

              {/* Staff Registration */}
              <Route path="/staff-register" element={<StaffRegisterPage />} />

              {/* Public Booking */}
              <Route path="/book/:slug" element={<PublicBookingPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/schedule"
                element={
                  <ProtectedRoute>
                    <SchedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services"
                element={
                  <ProtectedRoute>
                    <ServicesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff"
                element={
                  <ProtectedRoute>
                    <StaffPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/payments"
                element={
                  <ProtectedRoute>
                    <PaymentMethodsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/delete-acc"
                element={
                  <ProtectedRoute>
                    <DeleteAccountPage />
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
      </DarkModeProvider>
  );
};

export default App;

